import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditStandard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStandard } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  cardId: string
  emphasisLevel: CardStandard["emphasisLevel"]
  standards: Array<CardStandard>
  previousStandards: Array<string>
}

export const LESSON_STANDARDS_EMPHASIS = {
  name: "LESSON_STANDARDS_EMPHASIS",

  patches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.cardId,
    }).set("embedded", "attributes.standards", payload.standards).patch

    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.cardId,
    }).set(
      "embedded",
      "attributes.standards",
      JSON.parse(JSON.stringify(payload.previousStandards))
    ).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let newEvent: LessonEditStandard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "standards",

      adverbial_verb: "starring",
      adverbial_object_type: "standard",
      adverbial_object_specifier: "lesson",
      adverbial_object_id: payload.cardId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    let title =
      payload.emphasisLevel === null
        ? "Removed emphasis on standard"
        : payload.emphasisLevel === "STARRED"
        ? "Starred standard"
        : "Expanded standard"

    return {
      title: title,
      titleForAnalytics: "Lesson - Toggle Standard Emphasis",
      context: Object.assign(defaultLessonContext(payload, models), {
        newEmphasisLevel: payload.emphasisLevel,
      }),
      activity: defaultLessonActivity(payload, models),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_STANDARDS_EMPHASIS
